import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronsUpDown, Check, type LucideIcon } from 'lucide-react';
import baseapi from '../services/baseapi';

interface DropdownItem {
    id?: string | number | null;
    [key: string]: any;
}

interface SingleSelectLocalSearchApiDropdownProps {
    label?: string;
    value?: DropdownItem | null;
    onChange: (item: DropdownItem | null) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    apiEndpoint: string;
    icon: LucideIcon;
    searchPlaceholder?: string;
    searchInputPlaceholder: string;
    error?: string;
    required?: boolean;
    displayProperty: string;
    secondaryDisplayProperty?: string;
    searchFields: string[];
    additionalQueries?: Record<string, any>;
    additionalParams?: Record<string, any>;
    getDisplayText?: (item: DropdownItem) => string;
    idProperty?: string;
    buttonClassName?: string;
}


const SingleSelectLocalSearchApiDropdown = React.memo<SingleSelectLocalSearchApiDropdownProps>(({
    label,
    value,
    onChange,
    disabled = false,
    placeholder = "Select item",
    className = "",
    apiEndpoint,
    icon: Icon,
    searchInputPlaceholder,
    error,
    required,
    displayProperty,
    secondaryDisplayProperty,
    searchFields,
    additionalQueries = {},
    additionalParams = {},
    getDisplayText,
    idProperty = 'id',
    buttonClassName = ""
}) => {

    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [allItems, setAllItems] = useState<DropdownItem[]>([]);
    const [filteredItems, setFilteredItems] = useState<DropdownItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasLoaded, setHasLoaded] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Fetch data once on mount or when apiEndpoint changes
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await baseapi.get(apiEndpoint, {
                params: {
                    ...additionalQueries,
                    ...additionalParams
                }
            });

            if (response.data.status) {
                const data = response.data.data || [];
                setAllItems(data);
                setFilteredItems(data);
                setHasLoaded(true);
            }
        } catch (error) {
            console.error(`Error fetching data from ${apiEndpoint}:`, error);
        } finally {
            setIsLoading(false);
        }
    }, [apiEndpoint, JSON.stringify(additionalQueries), JSON.stringify(additionalParams)]);

    useEffect(() => {
        setHasLoaded(false);
        setAllItems([]);
        setFilteredItems([]);
    }, [apiEndpoint, JSON.stringify(additionalQueries), JSON.stringify(additionalParams)]);

    useEffect(() => {
        if (isOpen && !hasLoaded) {
            fetchData();
        }
    }, [isOpen, hasLoaded, fetchData]);

    // Handle local searching
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredItems(allItems);
            return;
        }

        const lowerTerm = searchTerm.toLowerCase();
        const filtered = allItems.filter(item => {
            return searchFields.some(field => {
                const val = item[field];
                return val && String(val).toLowerCase().includes(lowerTerm);
            });
        });
        setFilteredItems(filtered);
    }, [searchTerm, allItems, searchFields]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(target) &&
                menuRef.current && !menuRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const calculateDropdownPosition = useCallback(() => {
        if (!buttonRef.current) return;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 320;
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        let position: 'bottom' | 'top' = 'bottom';
        let style: React.CSSProperties = {
            width: `${buttonRect.width}px`,
            left: `${buttonRect.left}px`,
            position: 'fixed'
        };

        if (spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight + 20) {
            position = 'top';
            style.bottom = `${windowHeight - buttonRect.top + 8}px`;
        } else {
            position = 'bottom';
            style.top = `${buttonRect.bottom + 8}px`;
        }

        setDropdownPosition(position);
        setMenuStyle(style);
    }, []);

    const updatePosition = useCallback(() => {
        if (isOpen) {
            calculateDropdownPosition();
        }
    }, [isOpen, calculateDropdownPosition]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    const toggleDropdown = () => {
        if (disabled) return;
        if (!isOpen) {
            calculateDropdownPosition();
            setSearchTerm('');
        }
        setIsOpen(!isOpen);
    };

    const handleSelect = (item: DropdownItem) => {
        onChange(item);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    return (
        <div className={`space-y-1.5 ${className}`} ref={dropdownRef}>
            {label && (
                <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    {Icon && <Icon className="w-3.5 h-3.5 text-orange-500" aria-hidden="true" />}
                    {label}
                    {required && <span className='text-red-500'>*</span>}
                </label>
            )}

            <button
                ref={buttonRef}
                type="button"
                onClick={toggleDropdown}
                disabled={disabled}
                className={`group relative w-full cursor-pointer rounded-xl border-2 bg-slate-50 py-2.5 pl-3 pr-9 text-left text-sm shadow-sm transition-all duration-300
                    ${disabled ? 'opacity-50 cursor-not-allowed border-slate-100' : 'hover:border-slate-200 hover:bg-white'}
                    ${isOpen ? 'border-orange-500 bg-white ring-4 ring-orange-500/10' : error ? 'border-red-100' : 'border-slate-100'}
                    focus:outline-none ${buttonClassName}`}
            >
                <div className="flex items-center gap-2 overflow-hidden min-w-0 flex-1">
                    {value ? (
                        <div className="flex flex-col items-start min-w-0 text-left">
                            <span className="truncate text-slate-800 font-bold leading-tight">
                                {getDisplayText ? getDisplayText(value) : value[displayProperty]}
                            </span>
                            {secondaryDisplayProperty && value[secondaryDisplayProperty] && (
                                <span className="truncate text-[10px] text-slate-400 font-bold mt-0.5 leading-tight">
                                    {value[secondaryDisplayProperty]}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-slate-400 font-bold leading-tight truncate">{placeholder}</span>
                    )}
                </div>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
                    {value && !disabled && (
                        <button
                            onClick={handleClear}
                            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                            type="button"
                        >
                            <X className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                        </button>
                    )}
                    <span className="pointer-events-none flex items-center px-1">
                        <ChevronsUpDown className={`h-4 w-4 text-slate-400 transform transition-transform duration-200 ${isOpen ? 'rotate-180 text-orange-500' : ''}`} />
                    </span>
                </div>
            </button>

            {
                isOpen && createPortal(
                    <div
                        ref={menuRef}
                        className={`fixed z-[99999] bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 ${dropdownPosition === 'top' ? 'origin-bottom' : 'origin-top'}`}
                        style={{ ...menuStyle, maxHeight: '320px' }}
                    >
                        <div className="p-2 border-b border-slate-100 bg-slate-50/50 sticky top-0 z-10">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-sm text-slate-800 font-bold placeholder-slate-400 focus:border-orange-500 outline-none transition-all"
                                    placeholder={searchInputPlaceholder}
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="overflow-y-auto max-h-[260px] custom-scrollbar">
                            {isLoading && allItems.length === 0 ? (
                                <div className="p-6 text-center">
                                    <div className="w-5 h-5 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin mx-auto mb-2" />
                                    <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest">Loading...</span>
                                </div>
                            ) : filteredItems.length > 0 ? (
                                <div className="p-1">
                                    {filteredItems.map((item) => {
                                        const isSelected = value && item[idProperty] === value[idProperty];
                                        return (
                                            <button
                                                key={item[idProperty] || Math.random()}
                                                type="button"
                                                onClick={() => handleSelect(item)}
                                                className={`w-full flex items-center justify-between px-2.5 py-2.5 rounded-lg text-sm transition-all group ${isSelected ? 'bg-orange-50 text-orange-600' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                            >
                                                <div className="flex flex-col items-start min-w-0 text-left w-full">
                                                    <span className={`truncate w-full text-left ${isSelected ? 'font-black' : 'font-bold'}`}>
                                                        {getDisplayText ? getDisplayText(item) : item[displayProperty]}
                                                    </span>
                                                    {secondaryDisplayProperty && item[secondaryDisplayProperty] && (
                                                        <span className="text-[10px] text-slate-400 font-bold truncate w-full mt-0.5 leading-tight">
                                                            {item[secondaryDisplayProperty]}
                                                        </span>
                                                    )}
                                                </div>
                                                {isSelected && <Check className="w-4 h-4 shrink-0 text-orange-600" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="p-8 text-center">
                                    <div className="p-3 bg-slate-50 rounded-full w-fit mx-auto mb-3">
                                        <Search className="w-5 h-5 text-slate-300" />
                                    </div>
                                    <p className="text-sm text-slate-800 font-black uppercase tracking-tight">No results found</p>
                                    <p className="text-xs text-slate-400 font-bold mt-1">Try a different search term</p>
                                </div>
                            )}
                        </div>
                    </div>,
                    document.body
                )
            }

            {error && <span className='text-red-500 text-[10px] font-black uppercase tracking-tight mt-1 flex items-center gap-1.5'>{error}</span>}
        </div>
    );
});

SingleSelectLocalSearchApiDropdown.displayName = 'SingleSelectLocalSearchApiDropdown';

export default SingleSelectLocalSearchApiDropdown;
